import { isNotNil } from '@/utils/object-util'

export interface ToURLSearchParamsOptions {
  /**
   * Determines how to serialize arrays.
   */
  arrayStyle?: 'comma' | 'multi-key' | 'multi-key-bracket'
}

/**
 * A serializable URL parameter value.
 */
type SerializableURLParamValue =
  | string
  | number
  | null
  | undefined
  | boolean
  | Date
  | SerializableURLParamValue[]

/**
 * Creates a {@link URLSearchParams} from the given object hash.
 *
 * @param object
 * @param options
 */
export function toURLSearchParams(
  object: Record<PropertyKey, SerializableURLParamValue>,
  options: ToURLSearchParamsOptions = {},
): URLSearchParams {
  const arrayStyle = options.arrayStyle || 'comma'
  const result = new URLSearchParams()
  for (const key of Object.keys(object)) {
    const value = object[key]
    if (Array.isArray(value)) {
      const mappedItems: string[] = []
      for (const item of value) {
        const itemParam = toURLSearchParamValue(item)
        if (!isNotNil(itemParam)) {
          continue
        }

        mappedItems.push(itemParam)
      }

      switch (arrayStyle) {
        case 'comma': {
          result.set(key, mappedItems.join(','))
          break
        }
        case 'multi-key':
        case 'multi-key-bracket': {
          const actualKey =
            arrayStyle === 'multi-key-bracket' ? `${key}[]` : key
          for (const mappedItem of mappedItems) {
            result.append(actualKey, mappedItem)
          }
        }
      }

      continue
    }

    const param = toURLSearchParamValue(value)
    if (isNotNil(param)) {
      result.set(key, param)
    }
  }

  return result
}

/**
 * Maps the given value to a URL search parameter value.
 * @param value
 */
function toURLSearchParamValue(
  value: SerializableURLParamValue,
): string | null {
  if (Number.isNaN(value)) {
    return null
  }

  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'string'
  ) {
    return value.toString()
  }

  if (value instanceof Date) {
    return value.toISOString()
  }

  return null
}

/**
 * Creates a URL given a baseUrl and the params.
 */
export function createUrlWithParams(
  baseUrl: string,
  params?: Record<string, string>,
): URL {
  const url = new URL(baseUrl)

  for (const key in params) {
    url.searchParams.set(key, params[key])
  }

  return url
}
